import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/hooks/useAuth";
import {
  DISMISS_ATTENDANCE_REQUEST_MUTATION,
  PENDING_ATTENDANCE_REQUESTS_QUERY,
  eventAttendancePath,
} from "../api/attendanceService";
import { PendingAttendanceRequest } from "../models/subscriberModel";

/**
 * Dismissable banner asking the organizer to record attendance. The backend returns only the
 * events whose window is open, so this component has no timing logic. It is a banner, never a
 * modal: it must not block the page.
 */
export const AttendanceRequestBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);

  const { data } = useQuery<{ pendingAttendanceRequests: PendingAttendanceRequest[] }>(
    PENDING_ATTENDANCE_REQUESTS_QUERY,
    { skip: !isAuthenticated, fetchPolicy: "cache-and-network" }
  );
  const [dismissRequest] = useMutation(DISMISS_ATTENDANCE_REQUEST_MUTATION);

  const requests = useMemo(
    () => (data?.pendingAttendanceRequests ?? []).filter((r) => !dismissed.has(r.eventId)),
    [data, dismissed]
  );

  // Keep the position valid when an item is dismissed or the list shrinks.
  useEffect(() => {
    if (index >= requests.length) setIndex(Math.max(0, requests.length - 1));
  }, [requests.length, index]);

  const onAdminArea = location.pathname.startsWith("/admin");
  const current = requests[index];
  if (!isAuthenticated || !current || onAdminArea) return null;

  // The roster page is where the banner leads: no point showing it there.
  const onRoster = requests.some((r) => location.pathname === eventAttendancePath(r.eventId));
  if (onRoster) return null;

  const dismiss = () => {
    const eventId = current.eventId;
    setDismissed((prev) => new Set(prev).add(eventId));
    dismissRequest({ variables: { eventId } }).catch((e) =>
      console.error("dismissAttendanceRequest failed", e)
    );
  };

  return (
    <div
      role="region"
      aria-label="Registra le presenze"
      className="fixed inset-x-2 bottom-20 z-40 mx-auto max-w-md rounded-xl border border-indigo-100 bg-white p-3 shadow-lg md:bottom-4"
    >
      <div className="flex items-start gap-2">
        {requests.length > 1 && (
          <button
            type="button"
            aria-label="Evento precedente"
            onClick={() => setIndex((i) => (i - 1 + requests.length) % requests.length)}
            className="mt-1 rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        )}

        <div className="min-w-0 flex-1">
          <p
            className="line-clamp-2 break-words text-sm font-semibold text-gray-900"
            title={current.title}
          >
            Registra le presenze di «{current.title}»
          </p>
          <p className="text-xs text-gray-500">
            {current.subscribersCount === 1
              ? "1 partecipante iscritto"
              : `${current.subscribersCount} partecipanti iscritti`}
            {requests.length > 1 && (
              <span className="ml-2" aria-label={`Evento ${index + 1} di ${requests.length}`}>
                {index + 1} / {requests.length}
              </span>
            )}
          </p>
          <Link
            to={eventAttendancePath(current.eventId)}
            className="mt-2 inline-block rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Registra le presenze
          </Link>
        </div>

        {requests.length > 1 && (
          <button
            type="button"
            aria-label="Evento successivo"
            onClick={() => setIndex((i) => (i + 1) % requests.length)}
            className="mt-1 rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}

        <button
          type="button"
          aria-label="Chiudi"
          onClick={dismiss}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default AttendanceRequestBanner;
